import {Box, Center, Spinner} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {RecordIdHttpModel} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  SnippetProps,
  RecordAuditPairResourceId,
  getRecordAuditPairResourceLabel,
  isRecordAuditPairResourceId,
} from '@dms/shared';

import {Nullish, useNavigate} from 'shared/web';

import {PairedRecordSnippetTitle} from './PairedRecordSnippetTitle';
import {PairedRecordView} from './PairedRecordView';
import {useBusinessCasePairedRecord} from './useBusinessCasePairedRecord';
import {useInterestPairedRecord} from './useInterestPairedRecord';
import {useRentalOrderPairedRecord} from './useRentalOrderPairedRecord';
import {useServiceCasePairedRecord} from './useServiceCasePairedRecord';
import {useServiceOrderPairedRecord} from './useServiceOrderPairedRecord';
import {useVehicleSnippet} from './useVehicleSnippet';
import {useWorkshopInterestPairedRecord} from './useWorkshopInterestPairedRecord';

export interface PairedRecordSnippetArgs {
  record: RecordIdHttpModel | null;
  isEditable: boolean;
  /** Forces the snippet into a loading skeleton while a just-picked/cleared record is being
   * persisted and refetched (mirrors the branch snippet), regardless of entity-query cache. */
  isUpdating?: boolean;
  onPair: () => void;
}

export interface PairedRecordSnippetResult {
  /** Main paired-entity snippet (or a "+ pair" placeholder when nothing is paired). */
  mainSnippet: SnippetProps;
  /** Human-readable entity number/code of the paired record (e.g. business case code), never the UUID. */
  recordTitle: string | Nullish;
  /** Vehicle snippet shown next to a non-vehicle record; null when the record IS a vehicle or has none. */
  vehicleSnippet: SnippetProps | null;
  /** Id of the linked vehicle (the record itself when it IS a vehicle); drives the header photo/gallery. */
  vehicleId: string | null;
}

const MAIN_TEST_ID = 'recordAuditDetail-header-recordSnippet';

const idFor = (
  record: RecordIdHttpModel | null,
  resourceId: RecordAuditPairResourceId
): string | null => (record?.resourceId === resourceId ? record.id : null);

export function usePairedRecordSnippet(args: PairedRecordSnippetArgs): PairedRecordSnippetResult {
  const navigate = useNavigate();
  const {record} = args;
  const resourceId = isRecordAuditPairResourceId(record?.resourceId) ? record.resourceId : null;

  const businessCase = useBusinessCasePairedRecord(idFor(record, 'BUSINESS_CASE'));
  const serviceCase = useServiceCasePairedRecord(idFor(record, 'SERVICE_CASE'));
  const serviceOrder = useServiceOrderPairedRecord(idFor(record, 'SERVICE_ORDER'));
  const rentalOrder = useRentalOrderPairedRecord(idFor(record, 'RENTAL_ORDER'));
  const interest = useInterestPairedRecord(idFor(record, 'INTEREST'));
  const workshopInterest = useWorkshopInterestPairedRecord(idFor(record, 'WORKSHOP_INTEREST'));

  const activeView: PairedRecordView | null = match(resourceId)
    .with('BUSINESS_CASE', () => businessCase)
    .with('SERVICE_CASE', () => serviceCase)
    .with('SERVICE_ORDER', () => serviceOrder)
    .with('RENTAL_ORDER', () => rentalOrder)
    .with('INTEREST', () => interest)
    .with('WORKSHOP_INTEREST', () => workshopInterest)
    .otherwise(() => null);

  // VEHICLE record -> the vehicle itself is the main entity; otherwise the linked vehicle.
  const primaryVehicleId =
    resourceId === 'VEHICLE' ? (record?.id ?? null) : (activeView?.vehicleIds[0] ?? null);
  const vehicle = useVehicleSnippet(primaryVehicleId);

  const placeholderSnippet: SnippetProps = {
    label: i18n.t('page.recordAudit.pairing.dialogTitle'),
    placeholder: {
      title: i18n.t('page.recordAudit.pairing.cta'),
      onClick: args.isEditable ? args.onPair : undefined,
    },
    'data-testid': MAIN_TEST_ID,
  };

  const baseMainSnippet = resolveMainSnippet({
    resourceId,
    record,
    activeView,
    vehicleSnippet: vehicle.snippet,
    placeholderSnippet,
    onOpenDetail: (path: string) => navigate(path),
  });

  // Wrap the title so the code links to detail and (while editable) edit/unpair buttons sit alongside it.
  const mainSnippet: SnippetProps = isNotNil(baseMainSnippet.title)
    ? {
        ...baseMainSnippet,
        title: (
          <PairedRecordSnippetTitle
            title={baseMainSnippet.title}
            onOpenDetail={baseMainSnippet.onClick}
            onEdit={args.isEditable ? args.onPair : undefined}
            data-testid={MAIN_TEST_ID}
          />
        ),
        onClick: undefined,
      }
    : baseMainSnippet;

  // Record loading (a just-picked/cleared record being persisted+refetched, or the paired entity's
  // own queries still resolving) is shown as a spinner *inside the record snippet* — the inline
  // loading convention — instead of the header-wide snippet skeleton. `isLoading: false` on the
  // loading snippet beats the panel's global skeleton flag so only this snippet shows the spinner.
  const isUpdating = args.isUpdating ?? false;
  const isRecordLoading =
    isUpdating || (activeView?.isLoading ?? false) || (vehicle.snippet?.isLoading ?? false);
  const baseVehicleSnippet = resourceId === 'VEHICLE' ? null : vehicle.snippet;
  const loadingSnippet: SnippetProps = {
    label: i18n.t('page.recordAudit.pairing.dialogTitle'),
    title: (
      <Box paddingVertical={2}>
        <Center justify="flex-start">
          <Spinner size="small" />
        </Center>
      </Box>
    ),
    // Keep this snippet out of EntityHeader's own skeleton — it shows the inline spinner instead.
    isLoading: false,
    'data-testid': MAIN_TEST_ID,
  };
  const mainSnippetOut: SnippetProps = isRecordLoading ? loadingSnippet : mainSnippet;
  const vehicleSnippetOut: SnippetProps | null = isRecordLoading ? null : baseVehicleSnippet;

  // Plain-string entity number for non-header consumers (e.g. wizard step subtitle). Same source
  // as the header title (activeView.code / vehicle name), never the UUID.
  const recordTitle: string | Nullish = match(resourceId)
    .with('VEHICLE', () =>
      typeof vehicle.snippet?.title === 'string' ? vehicle.snippet.title : undefined
    )
    .otherwise(() => activeView?.code);

  return {
    mainSnippet: mainSnippetOut,
    recordTitle,
    vehicleSnippet: vehicleSnippetOut,
    // Hide the header photo while the record is changing so the previous vehicle's gallery doesn't
    // linger next to the new record.
    vehicleId: isRecordLoading ? null : primaryVehicleId,
  };
}

interface ResolveMainSnippetArgs {
  resourceId: RecordAuditPairResourceId | null;
  record: RecordIdHttpModel | null;
  activeView: PairedRecordView | null;
  vehicleSnippet: SnippetProps | null;
  placeholderSnippet: SnippetProps;
  onOpenDetail: (path: string) => void;
}

const resolveMainSnippet = (args: ResolveMainSnippetArgs): SnippetProps =>
  match(args)
    .with({resourceId: 'VEHICLE'}, () => args.vehicleSnippet ?? args.placeholderSnippet)
    .with(
      {resourceId: Pattern.not(Pattern.nullish), activeView: Pattern.not(Pattern.nullish)},
      ({resourceId, activeView}) => {
        const detailPath = activeView.detailPath;
        return {
          label: getRecordAuditPairResourceLabel(resourceId),
          title: activeView.code,
          onClick: isNotNil(detailPath) ? () => args.onOpenDetail(detailPath) : undefined,
          parameters: activeView.parameters,
          isLoading: activeView.isLoading,
          'data-testid': MAIN_TEST_ID,
        };
      }
    )
    // Paired to a resource without a dedicated snippet yet (e.g. CUSTOMER).
    .with({record: Pattern.not(Pattern.nullish)}, ({record}) => ({
      label: record.resourceId,
      title: record.id,
      'data-testid': MAIN_TEST_ID,
    }))
    .otherwise(() => args.placeholderSnippet);
